import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';

/* ─────────────────────────────────────────────────────────────
   ERPForce PDF Builder — document schema
   Per doc type: meta (name/icon/accent) · field tree · sample data
   Exposes export const PBData = { DOCS, order }
   ───────────────────────────────────────────────────────────── */

  // shared issuer (the ERP tenant)
  const COMPANY = {
    name: 'ALTRAD SPAR KSA',
    line1: 'King Abdullah Road, PO Box 1396',
    line2: 'Al-Jubail 31951, Kingdom of Saudi Arabia',
    phone: '+966 (13) 361 9096',
    email: 'info@arabianspar.com',
    web: 'www.arabianspar.com',
    trn: '300456789100003',
  };
  const BANK = {
    name: 'Saudi National Bank (SNB)',
    account: 'ALTRAD SPAR KSA',
    iban: 'SA44 1000 0000 1004 8821 3000',
    swift: 'NCBKSAJE',
  };
  const CUSTOMER = {
    name: 'Etisalat Digital — Smart Solutions',
    attn: 'Procurement Dept.',
    line1: 'Etisalat Tower 2, Sheikh Zayed Road',
    line2: 'Dubai, United Arab Emirates',
    trn: '100200300400500',
    email: 'ap@etisalat.ae',
  };

  // a believable line-item set (enough to push a 2nd page)
  const ITEMS = [
    { sku: 'EQ-7741', name: 'Industrial-grade network switch, 48-port managed', detail: 'Layer-3, PoE+, 740W', qty: 12, unit: 'pcs', price: 2150.0, tax: 5, disc: 0 },
    { sku: 'EQ-3320', name: 'Rack-mount UPS 3kVA online double-conversion', detail: 'With extended battery module', qty: 6, unit: 'pcs', price: 4380.0, tax: 5, disc: 5 },
    { sku: 'CB-1102', name: 'Cat6A S/FTP cabling, 305m drum', detail: 'LSZH, blue', qty: 20, unit: 'drum', price: 690.0, tax: 5, disc: 0 },
    { sku: 'EQ-5560', name: '42U server cabinet, 800×1000mm', detail: 'Perforated doors, cable mgmt', qty: 4, unit: 'pcs', price: 3120.0, tax: 5, disc: 0 },
    { sku: 'SV-0099', name: 'On-site installation & commissioning', detail: 'Certified engineers, 3 days', qty: 1, unit: 'lot', price: 8500.0, tax: 5, disc: 0 },
    { sku: 'EQ-2280', name: 'Fibre patch panel 24-port LC duplex', detail: 'OS2 single-mode', qty: 8, unit: 'pcs', price: 540.0, tax: 5, disc: 0 },
    { sku: 'AC-4410', name: 'Precision cooling unit, in-row 12kW', detail: 'Chilled-water, EC fans', qty: 2, unit: 'pcs', price: 18900.0, tax: 5, disc: 10 },
    { sku: 'EQ-6612', name: 'KVM-over-IP console, 16-port', detail: '1U, dual-power', qty: 3, unit: 'pcs', price: 2780.0, tax: 5, disc: 0 },
    { sku: 'CB-2204', name: 'Power distribution unit, metered 32A', detail: 'Vertical, 24 outlets', qty: 8, unit: 'pcs', price: 1240.0, tax: 5, disc: 0 },
    { sku: 'SV-0102', name: 'Annual maintenance contract (Tier-1)', detail: '24×7 response, 12 months', qty: 1, unit: 'yr', price: 22000.0, tax: 5, disc: 0 },
    { sku: 'EQ-8810', name: 'Environmental monitoring sensor kit', detail: 'Temp/humidity/leak, 12 nodes', qty: 1, unit: 'kit', price: 6400.0, tax: 5, disc: 0 },
    { sku: 'CB-3300', name: 'Overhead cable tray system, 300mm', detail: 'Hot-dip galvanised, per metre', qty: 60, unit: 'm', price: 78.0, tax: 5, disc: 0 },
  ];
  // precompute amounts + totals
  function priced(items) {
    let sub = 0, taxTotal = 0, discTotal = 0;
    const rows = items.map(it => {
      const gross = it.qty * it.price;
      const discAmt = gross * (it.disc || 0) / 100;
      const net = gross - discAmt;
      const taxAmt = net * (it.tax || 0) / 100;
      sub += net; taxTotal += taxAmt; discTotal += discAmt;
      return { ...it, amount: net };
    });
    return { rows, subtotal: sub, tax: taxTotal, discount: discTotal, total: sub + taxTotal };
  }
  const P = priced(ITEMS);

  // ── field-tree helper ────────────────────────────────────
  const f = (tag, label) => ({ tag, label });
  const loop = (tag, label) => ({ tag, label, loop: true });

  const COMPANY_GROUP = { key: 'company', label: 'Company', icon: 'bank', fields: [
    f('{{company.name}}', 'Name'), f('{{company.line1}}', 'Address 1'), f('{{company.line2}}', 'Address 2'),
    f('{{company.phone}}', 'Phone'), f('{{company.email}}', 'Email'), f('{{company.web}}', 'Website'), f('{{company.trn}}', 'Tax / TRN'),
  ]};
  const ITEMS_GROUP = { key: 'items', label: 'Line items', icon: 'list', fields: [
    loop('{{#each items}} … {{/each}}', 'Repeat for every item'),
    f('{{@number}}', 'Row number'), f('{{this.sku}}', 'SKU / code'), f('{{this.name}}', 'Description'),
    f('{{this.detail}}', 'Sub-detail'), f('{{qty this.qty}}', 'Quantity'), f('{{this.unit}}', 'Unit'),
    f('{{money this.price}}', 'Unit price'), f('{{pct this.disc}}', 'Discount %'), f('{{pct this.tax}}', 'Tax %'),
    f('{{money this.amount}}', 'Line amount'),
  ]};
  const TOTALS_GROUP = { key: 'totals', label: 'Totals', icon: 'sum', fields: [
    f('{{money totals.subtotal}}', 'Subtotal'), f('{{money totals.discount}}', 'Discount'),
    f('{{money totals.tax}}', 'Tax / VAT'), f('{{money totals.total}}', 'Grand total'), f('{{totals.words}}', 'Amount in words'),
  ]};
  const SIGN_GROUP = { key: 'signoff', label: 'Sign-off & footer', icon: 'check', fields: [
    f('{{notes}}', 'Notes'), f('{{#each terms}} … {{/each}}', 'Terms list'), f('{{signatory.name}}', 'Signatory'),
    f('{{signatory.role}}', 'Signatory role'),
  ]};
  const partyGroup = (key, label) => ({ key, label, icon: 'users', fields: [
    f(`{{${key}.name}}`, 'Name'), f(`{{${key}.attn}}`, 'Attention'), f(`{{${key}.line1}}`, 'Address 1'),
    f(`{{${key}.line2}}`, 'Address 2'), f(`{{${key}.trn}}`, 'Tax / TRN'), f(`{{${key}.email}}`, 'Email'),
  ]});
  const metaGroup = (fields) => ({ key: 'doc', label: 'Document', icon: 'file', fields });

  const TERMS_QUOTE = ['Prices are quoted in AED and valid for 30 days from the date of issue.',
    'Delivery within 4–6 weeks of confirmed purchase order and advance payment.',
    '50% advance with PO, balance against delivery. Prices exclusive of site works unless stated.'];

  // ══════════════ KSA bilingual (ZATCA) Tax Invoice ════════════
  // A self-contained Saudi tax invoice: its own bilingual seller/buyer
  // blocks (EN + AR + CRN + VAT), 15% VAT line items, ZATCA totals and
  // a QR. Amounts use plain figures (currency '' ) with "SAR" baked into
  // the labels, exactly like a compliant KSA invoice.
  const KSA_SELLER = {
    nameEn: 'ALTRAD SPAR KSA', nameAr: 'ألتراد سبار',
    addr1En: 'King Abdullah Road, PO Box 1396', addr1Ar: 'طريق الملك عبدالله، ص.ب ١٣٩٦',
    addr2En: 'Al-Jubail 31951, Kingdom of Saudi Arabia', addr2Ar: 'الجبيل ٣١٩٥١، المملكة العربية السعودية',
    crn: '1010488213', vat: '300456789100003',
  };
  const KSA_BUYER = {
    nameEn: 'Saudi Modern Contracting Company', nameAr: 'الشركة السعودية للمقاولات الحديثة',
    addr1En: 'King Fahd Rd, Al Murabba District, Building 6210', addr1Ar: 'طريق الملك فهد، حي المربع، مبنى ٦٢١٠',
    addr2En: 'Al Jubail 35514, Kingdom of Saudi Arabia', addr2Ar: 'الجبيل ٣٥٥١٤، المملكة العربية السعودية',
    crn: '2055041188', vat: '311388607100003',
  };
  const KSA_BANK = {
    nameEn: 'ALTRAD SPAR KSA', nameAr: 'ألتراد سبار',
    bankEn: 'Saudi National Bank (SNB)', bankAr: 'البنك الأهلي السعودي',
    branchEn: 'Al-Jubail', branchAr: 'الجبيل',
    account: 'SNB-100 488 2130', iban: 'SA44 1000 0000 1004 8821 3000', swift: 'NCBKSAJE',
  };
  const KSA_ITEMS = [
    { sku: 'SCF-1200', nameEn: 'Scaffolding rental charges as per attached specifications', nameAr: 'تكاليف تأجير السقالات وفقاً للمواصفات المرفقة', qty: 1, unitEn: 'Month', unitAr: 'شهر', price: 4250.0, disc: 0, tax: 15 },
    { sku: 'SCF-2010', nameEn: 'Aluminium mobile access tower, 5.2 m working height', nameAr: 'برج وصول ألمنيوم متحرك، ارتفاع عمل ٥٫٢ م', qty: 3, unitEn: 'pcs', unitAr: 'قطعة', price: 980.0, disc: 0, tax: 15 },
    { sku: 'SAF-3300', nameEn: 'Site safety netting & toe boards, supply & fix', nameAr: 'شبكات السلامة وألواح القدم بالموقع، توريد وتركيب', qty: 120, unitEn: 'm²', unitAr: 'م²', price: 18.0, disc: 0, tax: 15 },
    { sku: 'LBR-0050', nameEn: 'Erection & dismantling labour, certified crew', nameAr: 'أعمال التركيب والفك بطاقم معتمد', qty: 1, unitEn: 'lot', unitAr: 'دفعة', price: 2600.0, disc: 5, tax: 15 },
    { sku: 'LOG-0090', nameEn: 'Transportation & on-site logistics', nameAr: 'النقل والخدمات اللوجستية بالموقع', qty: 1, unitEn: 'trip', unitAr: 'رحلة', price: 750.0, disc: 0, tax: 15 },
  ];
  function pricedKsa(items) {
    let gross = 0, disc = 0, net = 0, vat = 0;
    const rows = items.map(it => {
      const g = it.qty * it.price;
      const d = g * (it.disc || 0) / 100;
      const taxable = g - d;
      const taxAmt = taxable * (it.tax || 0) / 100;
      gross += g; disc += d; net += taxable; vat += taxAmt;
      return { ...it, name: it.nameEn, amount: taxable, gross: g, discAmt: d, taxable, taxAmt, total: taxable + taxAmt };
    });
    return { rows, totals: { gross, discount: disc, exclVat: net, vatAmount: vat, inclVat: net + vat, balanceDue: net + vat, subtotal: net, tax: vat, total: net + vat } };
  }
  const KP = pricedKsa(KSA_ITEMS);

  const ksaPartyArGroup = (key, label) => ({ key, label, icon: 'users', fields: [
    f(`{{${key}.name}}`, 'Name (AR)'),
    f(`{{${key}.line1}}`, 'Address 1 (AR)'),
    f(`{{${key}.line2}}`, 'Address 2 (AR)'),
  ]});

  const DOCS = {
    quotation: {
      id: 'quotation', name: 'Quotation', full: 'Quotation / Estimate', icon: 'quote', accent: 'acc-green', docAccent: '#1A8A56',
      desc: 'Priced offer with validity, line items and terms.',
      includes: [{ flag: 'showTerms', label: 'Terms' }],
      groups: [COMPANY_GROUP, partyGroup('customer', 'Customer'),
        metaGroup([f('{{doc.number}}', 'Quote no.'), f('{{doc.date}}', 'Date'), f('{{doc.validUntil}}', 'Valid until'),
          f('{{doc.salesRep}}', 'Sales rep'), f('{{doc.currency}}', 'Currency'), f('{{doc.reference}}', 'Customer ref')]),
        ITEMS_GROUP, TOTALS_GROUP, SIGN_GROUP],
      sample: {
        company: COMPANY, customer: CUSTOMER, currency: 'AED',
        doc: { number: 'QT-2026-0418', date: '30 May 2026', validUntil: '29 Jun 2026', salesRep: 'L. Haddad', reference: 'RFQ-ETD-7741' },
        items: P.rows, totals: { ...totalsOf(P), words: 'Three hundred eighteen thousand nine hundred forty-two AED only' },
        notes: 'Thank you for the opportunity to quote. We would be pleased to arrange a site survey at your convenience.',
        terms: TERMS_QUOTE, signatory: { name: 'L. Haddad', role: 'Sales Manager' },
      },
    },
    invoice: {
      id: 'invoice', name: 'Tax Invoice', full: 'Tax Invoice', icon: 'doc', accent: 'acc-navy', docAccent: '#1B2C6E', family: 'invoice',
      desc: 'VAT-compliant invoice with TRN, tax breakdown and payment details.',
      includes: [{ flag: 'showBank', label: 'Payment details' }],
      groups: [COMPANY_GROUP, partyGroup('customer', 'Bill to'),
        metaGroup([f('{{doc.number}}', 'Invoice no.'), f('{{doc.date}}', 'Issue date'), f('{{doc.dueDate}}', 'Due date'),
          f('{{doc.po}}', 'PO number'), f('{{doc.terms}}', 'Payment terms'), f('{{doc.status}}', 'Status')]),
        ITEMS_GROUP, TOTALS_GROUP,
        { key: 'pay', label: 'Payment', icon: 'wallet', fields: [f('{{bank.name}}', 'Bank'), f('{{bank.iban}}', 'IBAN'), f('{{bank.swift}}', 'SWIFT'), f('{{bank.account}}', 'Account name')] },
        SIGN_GROUP],
      sample: {
        company: COMPANY, customer: CUSTOMER, bank: BANK, currency: 'AED',
        doc: { number: 'INV-2026-1183', date: '30 May 2026', dueDate: '29 Jun 2026', po: 'PO-ETD-55021', terms: 'Net 30', status: 'due' },
        items: P.rows, totals: { ...totalsOf(P), words: 'Three hundred eighteen thousand nine hundred forty-two AED only' },
        notes: 'Please quote the invoice number on all remittances. Late payments may attract 1.5% monthly interest.',
        terms: ['Goods remain the property of Falcon Industrial Trading LLC until paid in full.'],
        signatory: { name: 'S. Khan', role: 'Finance Controller' },
      },
    },
    invoice_ksa: {
      id: 'invoice_ksa', name: 'Tax Invoice · KSA', full: 'Tax Invoice — KSA (Bilingual)', icon: 'doc', accent: 'acc-green', docAccent: '#0E7A53', family: 'invoice',
      desc: 'ZATCA-ready Saudi tax invoice — English & Arabic, 15% VAT, QR.',
      includes: [{ flag: 'showBank', label: 'Bank details' }, { flag: 'showQr', label: 'ZATCA QR' }],
      groups: [ksaPartyArGroup('companyAr', 'Seller (Arabic)'), ksaPartyArGroup('customerAr', 'Buyer (Arabic)'),
        metaGroup([f('{{doc.number}}', 'Invoice no.'), f('{{doc.date}}', 'Invoice date'), f('{{doc.supplyDate}}', 'Supply date'),
          f('{{doc.dueDate}}', 'Due date'), f('{{doc.periodFrom}}', 'Period from'), f('{{doc.periodTo}}', 'Period to'),
          f('{{doc.po}}', 'Contract / PO no.'), f('{{doc.project}}', 'Project / ref.')]),
        { key: 'items', label: 'Line items', icon: 'list', fields: [
          loop('{{#each items}} … {{/each}}', 'Repeat for every item'), f('{{@number}}', 'Row number'),
          f('{{this.sku}}', 'Code / SKU'), f('{{this.name}}', 'Description (EN)'), f('{{this.nameAr}}', 'Description (AR)'),
          f('{{qty this.qty}}', 'Quantity'), f('{{this.unitEn}}', 'Unit (EN)'), f('{{this.unitAr}}', 'Unit (AR)'),
          f('{{money this.price}}', 'Unit rate'), f('{{money this.taxable}}', 'Taxable amount'), f('{{pct this.tax}}', 'Tax rate'),
          f('{{money this.taxAmt}}', 'Tax amount'), f('{{money this.total}}', 'Total with tax') ] },
        { key: 'totals', label: 'Totals', icon: 'sum', fields: [
          f('{{money totals.gross}}', 'Gross total'), f('{{money totals.discount}}', 'Total discount'),
          f('{{money totals.exclVat}}', 'Total excl. VAT'), f('{{money totals.vatAmount}}', 'VAT amount'),
          f('{{money totals.inclVat}}', 'Amount incl. VAT'), f('{{money totals.balanceDue}}', 'Balance due'),
          f('{{words.en}}', 'Amount in words (EN)'), f('{{words.ar}}', 'Amount in words (AR)') ] },
        { key: 'bank', label: 'Bank details', icon: 'wallet', fields: [
          f('{{bank.nameEn}}', 'Account name'), f('{{bank.bankEn}}', 'Bank'), f('{{bank.branchEn}}', 'Branch'),
          f('{{bank.account}}', 'Account no.'), f('{{bank.iban}}', 'IBAN'), f('{{bank.swift}}', 'SWIFT') ] },
      ],
      sample: {
        company: COMPANY, currency: '', companyAr: { name: 'ألتراد سبار', line1: 'طريق 124 ، الجبيل 31951', line2: 'المملكة العربية السعودية' }, customerAr: { name: 'المتحدة للإنشاءات', line1: 'ص.ب 8871، الرياض', line2: 'المملكة العربية السعودية' }, bank: KSA_BANK,
        doc: { number: 'INV-KSA-2026-0734', date: '30 May 2026', supplyDate: '30 May 2026', dueDate: '29 Jun 2026',
          periodFrom: '01 May 2026', periodTo: '31 May 2026', po: 'SMC-PO-0202-26', project: 'Scaffolding — Jubail' },
        items: KP.rows, totals: KP.totals,
        words: { en: 'Fourteen Thousand Four Hundred Fifty-Five Saudi Riyals and Fifty Halalas only',
          ar: 'أربعة عشر ألفاً وأربعمائة وخمسة وخمسون ريالاً سعودياً وخمسون هللة لا غير' },
        notes: 'This is a system-generated tax invoice. No signature is required. هذه فاتورة ضريبية صادرة آلياً ولا تتطلب توقيعاً.',
        signatory: { name: '', role: '' },
      },
    },
    delivery: {
      id: 'delivery', name: 'Delivery Note', full: 'Delivery Note', icon: 'truck', accent: 'acc-amber', docAccent: '#8a540b',
      desc: 'Goods despatch note — quantities only, received-by sign-off.',
      groups: [COMPANY_GROUP, partyGroup('shipTo', 'Ship to'), partyGroup('customer', 'Customer'),
        metaGroup([f('{{doc.number}}', 'DN number'), f('{{doc.date}}', 'Despatch date'), f('{{doc.invoice}}', 'Invoice ref'),
          f('{{doc.po}}', 'PO number'), f('{{doc.vehicle}}', 'Vehicle'), f('{{doc.driver}}', 'Driver')]),
        { key: 'items', label: 'Line items', icon: 'list', fields: [
          loop('{{#each items}} … {{/each}}', 'Repeat for every item'), f('{{@number}}', 'Row number'),
          f('{{this.sku}}', 'SKU / code'), f('{{this.name}}', 'Description'), f('{{qty this.qty}}', 'Qty ordered'),
          f('{{qty this.delivered}}', 'Qty delivered'), f('{{this.unit}}', 'Unit') ] },
        SIGN_GROUP],
      sample: {
        company: COMPANY, customer: CUSTOMER, shipTo: { name: 'Etisalat DC — Site B', attn: 'Goods Inwards', line1: 'Dubai Investment Park 2', line2: 'Dubai, UAE', email: '' }, currency: 'AED',
        doc: { number: 'DN-2026-0902', date: '30 May 2026', invoice: 'INV-2026-1183', po: 'PO-ETD-55021', vehicle: 'Mercedes Actros · D-44192', driver: 'M. Iqbal' },
        items: P.rows.map(r => ({ ...r, delivered: r.qty })),
        notes: 'Please inspect all goods on receipt. Shortages or damage must be noted below and reported within 48 hours.',
        terms: [], signatory: { name: '', role: '' },
      },
    },
    po: {
      id: 'po', name: 'Purchase Order', full: 'Purchase Order', icon: 'cart', accent: 'acc-blue', docAccent: '#1F4FA8',
      desc: 'Order placed on a supplier — quantities, prices and delivery terms.',
      includes: [{ flag: 'showTerms', label: 'Terms' }],
      groups: [COMPANY_GROUP, partyGroup('vendor', 'Supplier'), partyGroup('shipTo', 'Deliver to'),
        metaGroup([f('{{doc.number}}', 'PO number'), f('{{doc.date}}', 'Order date'), f('{{doc.requiredBy}}', 'Required by'),
          f('{{doc.buyer}}', 'Buyer'), f('{{doc.incoterms}}', 'Incoterms'), f('{{doc.currency}}', 'Currency')]),
        ITEMS_GROUP, TOTALS_GROUP, SIGN_GROUP],
      sample: {
        company: COMPANY,
        vendor: { name: 'Gulf Components FZE', attn: 'Sales Desk', line1: 'Jebel Ali Free Zone, South', line2: 'Dubai, UAE', trn: '100998877600002', email: 'orders@gulfcomp.ae' },
        shipTo: { name: 'Falcon Warehouse — JAFZA', attn: 'Stores', line1: 'Plot S-40701, JAFZA South', line2: 'Dubai, UAE', email: '' }, currency: 'AED',
        doc: { number: 'PO-2026-3340', date: '30 May 2026', requiredBy: '20 Jun 2026', buyer: 'A. Okoro', incoterms: 'DDP — Jebel Ali' },
        items: P.rows.slice(0, 8), totals: { ...totalsOf(priced(ITEMS.slice(0, 8))), words: '' },
        notes: 'This PO is governed by Falcon Industrial standard purchasing terms. Reference the PO number on all documents and invoices.',
        terms: ['Deliver in full unless partial delivery is agreed in writing.', 'Invoices without a valid PO number will be returned.'],
        signatory: { name: 'A. Okoro', role: 'Procurement Lead' },
      },
    },
    report: {
      id: 'report', name: 'Financial Report', full: 'Financial Report (P&L)', icon: 'barChart', accent: 'acc-teal', docAccent: '#156B66',
      desc: 'Statement-style report — grouped sections, subtotals, KPIs.',
      includes: [{ flag: 'showKpis', label: 'Key figures' }],
      groups: [COMPANY_GROUP,
        metaGroup([f('{{doc.title}}', 'Report title'), f('{{doc.period}}', 'Period'), f('{{doc.basis}}', 'Basis'),
          f('{{doc.currency}}', 'Currency'), f('{{doc.preparedBy}}', 'Prepared by'), f('{{doc.generated}}', 'Generated')]),
        { key: 'kpis', label: 'KPIs', icon: 'target', fields: [loop('{{#each kpis}} … {{/each}}', 'Repeat for each KPI'), f('{{this.label}}', 'Label'), f('{{this.value}}', 'Value'), f('{{this.sub}}', 'Caption')] },
        { key: 'rows', label: 'Statement rows', icon: 'table', fields: [
          loop('{{#each rows}} … {{/each}}', 'Repeat for each row'), f('{{this.label}}', 'Line label'),
          f('{{money this.current}}', 'Current period'), f('{{money this.prior}}', 'Prior period'),
          f('{{pct this.pctRevenue}}', '% of revenue'), f('{{this.kind}}', 'Row kind (section/sub/grand)') ] },
        SIGN_GROUP],
      sample: {
        company: COMPANY, currency: 'AED',
        doc: { title: 'Statement of Profit & Loss', period: '1 Jan – 31 May 2026', basis: 'Accrual', preparedBy: 'R. Mehta', generated: '30 May 2026 14:08' },
        kpis: [ { label: 'Revenue', value: 'AED 8.42M', sub: '+12.4% YoY' }, { label: 'Gross margin', value: '41.8%', sub: '+1.9 pts' },
          { label: 'EBITDA', value: 'AED 1.96M', sub: '23.3% of rev' }, { label: 'Net profit', value: 'AED 1.18M', sub: '14.0% of rev' } ],
        rows: REPORT_ROWS(),
        notes: 'Figures are management accounts, unaudited. Prior-period comparatives restated for the reclassification of logistics costs.',
        terms: [], signatory: { name: 'R. Mehta', role: 'Group Financial Controller' },
      },
    },
    receipt: {
      id: 'receipt', name: 'Payment Receipt', full: 'Receipt / Payment voucher', icon: 'receipt', accent: 'acc-green', docAccent: '#156B43',
      desc: 'Confirmation that a payment has been received and allocated.',
      groups: [COMPANY_GROUP, partyGroup('customer', 'Received from'),
        metaGroup([f('{{doc.number}}', 'Receipt no.'), f('{{doc.date}}', 'Date'), f('{{doc.method}}', 'Method'),
          f('{{doc.reference}}', 'Txn reference'), f('{{doc.currency}}', 'Currency')]),
        { key: 'alloc', label: 'Allocation', icon: 'list', fields: [
          loop('{{#each allocations}} … {{/each}}', 'Repeat per invoice'), f('{{this.invoice}}', 'Invoice no.'),
          f('{{this.date}}', 'Invoice date'), f('{{money this.invoiceTotal}}', 'Invoice total'), f('{{money this.applied}}', 'Amount applied') ] },
        { key: 'amt', label: 'Amount', icon: 'sum', fields: [f('{{money received}}', 'Amount received'), f('{{words}}', 'In words'), f('{{money balance}}', 'Balance remaining')] },
        SIGN_GROUP],
      sample: {
        company: COMPANY, customer: CUSTOMER, currency: 'AED',
        doc: { number: 'RCPT-2026-0771', date: '30 May 2026', method: 'Bank transfer', reference: 'ENBD-TT-99041' },
        allocations: [
          { invoice: 'INV-2026-1102', date: '02 May 2026', invoiceTotal: 142500, applied: 142500 },
          { invoice: 'INV-2026-1140', date: '15 May 2026', invoiceTotal: 88200, applied: 88200 },
          { invoice: 'INV-2026-1183', date: '30 May 2026', invoiceTotal: 318942, applied: 119300 },
        ],
        received: 350000, balance: 199642, words: 'Three hundred fifty thousand AED only',
        notes: 'This receipt is issued subject to realisation of the funds. Allocations applied oldest-first.',
        terms: [], signatory: { name: 'S. Khan', role: 'Finance Controller' },
      },
    },
    credit: {
      id: 'credit', name: 'Credit Note', full: 'Credit Note', icon: 'minus', accent: 'acc-rose', docAccent: '#A8232A',
      desc: 'Reverses or reduces a prior invoice — VAT adjusted.',
      groups: [COMPANY_GROUP, partyGroup('customer', 'Bill to'),
        metaGroup([f('{{doc.number}}', 'Credit no.'), f('{{doc.date}}', 'Date'), f('{{doc.against}}', 'Against invoice'),
          f('{{doc.reason}}', 'Reason'), f('{{doc.currency}}', 'Currency')]),
        ITEMS_GROUP, TOTALS_GROUP, SIGN_GROUP],
      sample: {
        company: COMPANY, customer: CUSTOMER, currency: 'AED',
        doc: { number: 'CN-2026-0044', date: '30 May 2026', against: 'INV-2026-1140', reason: 'Goods returned — 2 units UPS faulty' },
        items: priced([
          { sku: 'EQ-3320', name: 'Rack-mount UPS 3kVA online double-conversion', detail: 'Returned — DOA', qty: 2, unit: 'pcs', price: 4380.0, tax: 5, disc: 5 },
          { sku: 'SV-CR01', name: 'Restocking adjustment', detail: 'Per returns policy', qty: 1, unit: 'lot', price: -320.0, tax: 5, disc: 0 },
        ]).rows,
        totals: { ...totalsOf(priced([{ qty: 2, price: 4380, tax: 5, disc: 5 }, { qty: 1, price: -320, tax: 5, disc: 0 }])), words: 'Eight thousand four hundred sixty-eight AED only' },
        notes: 'This credit note will be applied against your outstanding balance. VAT has been adjusted accordingly.',
        terms: [], signatory: { name: 'S. Khan', role: 'Finance Controller' },
      },
    },
    statement: {
      id: 'statement', name: 'Statement', full: 'Statement of Account', icon: 'list', accent: 'acc-slate', docAccent: '#3D4046',
      desc: 'Running ledger of invoices, credits and payments with aging.',
      groups: [COMPANY_GROUP, partyGroup('customer', 'Account'),
        metaGroup([f('{{doc.number}}', 'Statement no.'), f('{{doc.date}}', 'As at'), f('{{doc.period}}', 'Period'),
          f('{{doc.currency}}', 'Currency'), f('{{money doc.openingBalance}}', 'Opening balance')]),
        { key: 'ledger', label: 'Ledger lines', icon: 'list', fields: [
          loop('{{#each ledger}} … {{/each}}', 'Repeat per transaction'), f('{{this.date}}', 'Date'),
          f('{{this.ref}}', 'Reference'), f('{{this.type}}', 'Type'), f('{{money this.debit}}', 'Debit'),
          f('{{money this.credit}}', 'Credit'), f('{{money this.balance}}', 'Running balance') ] },
        { key: 'aging', label: 'Aging', icon: 'clock', fields: [f('{{money aging.current}}', 'Current'), f('{{money aging.d30}}', '1–30'), f('{{money aging.d60}}', '31–60'), f('{{money aging.d90}}', '61–90'), f('{{money aging.over}}', '90+'), f('{{money closingBalance}}', 'Closing balance')] },
        SIGN_GROUP],
      sample: {
        company: COMPANY, customer: CUSTOMER, currency: 'AED',
        doc: { number: 'SOA-2026-0512', date: '31 May 2026', period: '1 Jan – 31 May 2026', openingBalance: 0 },
        ledger: LEDGER(),
        aging: { current: 318942, d30: 119300, d60: 0, d90: 0, over: 0 }, closingBalance: 438242,
        notes: 'Please remit overdue amounts at your earliest convenience. Contact accounts@falconind.ae for any queries.',
        terms: [], signatory: { name: 'S. Khan', role: 'Finance Controller' },
      },
    },
  };

  function totalsOf(p) { return { subtotal: p.subtotal, discount: p.discount, tax: p.tax, total: p.total }; }

  function REPORT_ROWS() {
    return [
      { kind: 'section', label: 'Revenue' },
      { label: 'Product sales', current: 6120000, prior: 5480000, pctRevenue: 72.7 },
      { label: 'Service & support', current: 1840000, prior: 1510000, pctRevenue: 21.9 },
      { label: 'Other income', current: 460000, prior: 502000, pctRevenue: 5.4 },
      { kind: 'sub', label: 'Total revenue', current: 8420000, prior: 7492000, pctRevenue: 100 },
      { kind: 'section', label: 'Cost of sales' },
      { label: 'Materials & goods', current: -3980000, prior: -3610000, pctRevenue: -47.3 },
      { label: 'Direct labour', current: -920000, prior: -865000, pctRevenue: -10.9 },
      { kind: 'sub', label: 'Gross profit', current: 3520000, prior: 3017000, pctRevenue: 41.8 },
      { kind: 'section', label: 'Operating expenses' },
      { label: 'Salaries & benefits', current: -1080000, prior: -990000, pctRevenue: -12.8 },
      { label: 'Rent & facilities', current: -360000, prior: -348000, pctRevenue: -4.3 },
      { label: 'Marketing', current: -240000, prior: -180000, pctRevenue: -2.9 },
      { label: 'Depreciation', current: -210000, prior: -205000, pctRevenue: -2.5 },
      { label: 'General & admin', current: -312000, prior: -301000, pctRevenue: -3.7 },
      { kind: 'sub', label: 'Operating profit', current: 1118000, prior: 793000, pctRevenue: 13.3 },
      { label: 'Finance costs', current: -64000, prior: -71000, pctRevenue: -0.8 },
      { label: 'Tax (9%)', current: -94000, prior: -58000, pctRevenue: -1.1 },
      { kind: 'grand', label: 'Net profit for the period', current: 1180000, prior: 664000, pctRevenue: 14.0 },
    ];
  }
  function LEDGER() {
    const L = [
      { date: '02 Jan', ref: 'INV-2026-0911', type: 'Invoice', debit: 96400, credit: 0 },
      { date: '18 Jan', ref: 'RCPT-2026-0610', type: 'Payment', debit: 0, credit: 96400 },
      { date: '04 Feb', ref: 'INV-2026-0998', type: 'Invoice', debit: 154200, credit: 0 },
      { date: '20 Feb', ref: 'CN-2026-0031', type: 'Credit note', debit: 0, credit: 12200 },
      { date: '28 Feb', ref: 'RCPT-2026-0688', type: 'Payment', debit: 0, credit: 142000 },
      { date: '02 May', ref: 'INV-2026-1102', type: 'Invoice', debit: 142500, credit: 0 },
      { date: '15 May', ref: 'INV-2026-1140', type: 'Invoice', debit: 88200, credit: 0 },
      { date: '22 May', ref: 'RCPT-2026-0771', type: 'Payment', debit: 0, credit: 350000 },
      { date: '30 May', ref: 'INV-2026-1183', type: 'Invoice', debit: 318942, credit: 0 },
      { date: '30 May', ref: 'INV-2026-1190', type: 'Invoice', debit: 119300, credit: 0 },
    ];
    let bal = 0;
    return L.map(r => { bal += r.debit - r.credit; return { ...r, balance: bal }; });
  }

  export const PBData = { DOCS, order: ['quotation', 'invoice', 'invoice_ksa', 'delivery', 'po', 'report', 'receipt', 'credit', 'statement'] };

